import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import ConfettiExplosion from 'react-confetti-explosion';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../../../../firebase';
import { useAuth } from '../../../../hooks/useAuth';
import { CheckCircleIcon } from '@heroicons/react/solid';
import { resizeTextarea } from '../../../../utils/textareaResize';
import {
  emitFeedbackSubmitted,
  emitFeedbackPromptShown,
  emitFeedbackDismissed,
  roleFromUserType,
  assessmentTypeFromModule,
  // One string for both destinations: the Firestore formFeedback.type value
  // written below and the trigger field on the feedback events. The emitter
  // only records a trigger it recognises, so a second copy of this literal
  // would drop the field the moment the two drifted apart.
  FEEDBACK_TRIGGER_ALL_FORMS_COMPLETED,
} from '../../../../analytics/serviceUserFormEvents';

// Custom animations as a style tag
const customStyles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes scaleIn {
    from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
    to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  }
  
  @keyframes collapseHeight {
    from { 
      opacity: 1; 
      max-height: 200px; 
      margin-bottom: 0.75rem;
    }
    to { 
      opacity: 0; 
      max-height: 0; 
      margin-bottom: 0;
    }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out;
  }
  
  .animate-slideDown {
    animation: slideDown 0.4s ease-out;
  }
  
  .animate-scaleIn {
    animation: scaleIn 0.3s ease-out;
  }
  
  .animate-collapseHeight {
    animation: collapseHeight 0.4s ease-out forwards;
    overflow: hidden;
  }
`;

// `subjectPatientId` is the patient the questionnaires are ABOUT. It differs
// from the signed-in account only for informants, who answer about someone
// else; passing it keeps every event for one patient on one journey. Falls
// back to the account's own uid, which is correct for patients.
export default function AllFormsCompletedFeedbackModal({ isOpen, onClose, onFeedbackSubmitted, feedbackCollection = 'patients', subjectPatientId = null }) {
  const [showConfetti, setShowConfetti] = useState(false);
  const [taskRating, setTaskRating] = useState(0);
  const [platformRating, setPlatformRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const modalContentRef = useRef(null);
  const promptShownRef = useRef(false);
  const { claims } = useAuth();

  // Shared context for the three feedback events (prompt shown, dismissed,
  // submitted) so all three describe the same subject and cohort — otherwise
  // the funnel cannot be joined up.
  const feedbackContext = useCallback(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return null;
    return {
      patientId: subjectPatientId || uid,
      role: roleFromUserType(claims?.userType) ||
        (feedbackCollection === 'informants' ? 'informant' : 'patient'),
      assessmentType: assessmentTypeFromModule(claims?.assessmentModule) ||
        (feedbackCollection === 'informants' ? 'autism' : 'adhd'),
    };
  }, [claims?.userType, claims?.assessmentModule, feedbackCollection, subjectPatientId]);

  // Trigger confetti when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      // Reset state when modal opens
      setTaskRating(0);
      setPlatformRating(0);
      setFeedbackText('');
      setHasSubmitted(false);
    }
  }, [isOpen]);

  // Feedback funnel denominator: one prompt-shown per opening (the ref guards
  // against a re-run emitting twice for the same sitting).
  useEffect(() => {
    if (!isOpen) {
      promptShownRef.current = false;
      return;
    }
    if (promptShownRef.current) return;
    // Claimed only once the event is actually emitted. Setting it first meant
    // an opening where the context was not ready yet (claims still resolving)
    // burned the guard and never emitted, silently deflating the denominator
    // the funnel is measured against.
    const context = feedbackContext();
    if (!context) return;
    promptShownRef.current = true;
    emitFeedbackPromptShown(context, { trigger: FEEDBACK_TRIGGER_ALL_FORMS_COMPLETED });
  }, [isOpen, feedbackContext]);

  // Closing without submitting is the drop-off signal; the success step's own
  // close button runs after hasSubmitted, so it is not counted as a dismissal.
  const handleDismiss = () => {
    if (!hasSubmitted) {
      const context = feedbackContext();
      if (context) {
        emitFeedbackDismissed(context, {
          trigger: FEEDBACK_TRIGGER_ALL_FORMS_COMPLETED,
          taskRating,
          platformRating,
          hasText: (feedbackText || '').trim().length > 0,
        });
      }
    }
    onClose();
  };

  // Auto-scroll to bottom when feedback section appears
  useEffect(() => {
    if (taskRating > 0 && platformRating > 0 && modalContentRef.current) {
      // Brief delay to allow DOM updates and start of animations
      setTimeout(() => {
        modalContentRef.current.scrollTo({
          top: modalContentRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 250); // Reduced delay for snappier response
    }
  }, [taskRating, platformRating]);

  const handleTaskRatingSelect = (selectedRating) => {
    setTaskRating(selectedRating);
    // Clear feedback text when rating changes to get fresh input
    setFeedbackText('');
  };

  const handlePlatformRatingSelect = (selectedRating) => {
    setPlatformRating(selectedRating);
    // Clear feedback text when rating changes to get fresh input
    setFeedbackText('');
  };

  const handleSubmitFeedback = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const user = auth.currentUser;
      
      if (user) {
      // Capture the exact question text shown to the user at submit time so that
      // providers can see the original wording later without having to
      // recompute it from ratings. This also insulates us from future copy
      // changes in the modal.
      const feedbackPromptAtSubmit = (getFeedbackPrompt() || '').trim() || null;

        // Save feedback to Firestore under formFeedback key
        const patientRef = doc(db, feedbackCollection, user.uid);
        await updateDoc(patientRef, {
          'formFeedback': {
            taskRating: taskRating,
            platformRating: platformRating,
            feedback: feedbackText.trim() || null,
            submittedAt: serverTimestamp(),
          // Persist the exact question that was displayed to the user when they
          // wrote their feedback. This is optional metadata; older records may
          // not have it and downstream readers should treat it as nullable.
          question: feedbackPromptAtSubmit,
          type: FEEDBACK_TRIGGER_ALL_FORMS_COMPLETED
          }
        });
        
        console.log(`[AllFormsCompletedFeedbackModal] Feedback saved: Task Rating ${taskRating}, Platform Rating ${platformRating}, Feedback: "${feedbackText}"`);

        // Milestone analytics: ratings, a has-text flag, and the question we
        // showed (our own copy) — never the user's text. The subject and
        // cohort come from the same shared context as prompt_shown and
        // dismissed, so an informant's submission names the assessed patient
        // rather than the informant's own account.
        const submitContext = feedbackContext();
        if (submitContext) {
          emitFeedbackSubmitted(submitContext, {
            taskRating,
            platformRating,
            hasText: (feedbackText || '').trim().length > 0,
            question: feedbackPromptAtSubmit,
            trigger: FEEDBACK_TRIGGER_ALL_FORMS_COMPLETED,
          });
        }

        // Trigger alert only when written feedback is provided (server enforces too).
        const hasText = (feedbackText || '').trim().length > 0;
        if (hasText) {
          try {
            // Fire-and-forget; do not block the UI.
            // Include the question prompt so the email recipient can understand
            // what the user was responding to (the question varies by score).
            httpsCallable(functions, 'sendFormFeedbackAlertEmail')({
              taskRating: taskRating,
              platformRating: platformRating,
              feedback: (feedbackText || '').trim(),
              question: feedbackPromptAtSubmit || '',
              assessmentModule: claims?.assessmentModule || 'adultAdhd'
            })
            .then(() => console.log('[AllFormsCompletedFeedbackModal] Feedback alert triggered'))
            .catch(err => console.error('[AllFormsCompletedFeedbackModal] Error triggering feedback alert:', err));
          } catch (err) {
            console.error('[AllFormsCompletedFeedbackModal] Feedback alert invocation error:', err);
          }
        }
      }
      
      setHasSubmitted(true);
      
      // Call callback to refresh parent state
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted();
      }
      
    } catch (error) {
      console.error('[AllFormsCompletedFeedbackModal] Error saving feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFeedbackPrompt = () => {
    const worstRating = Math.min(taskRating, platformRating);
    if (worstRating <= 2) {
      return "We're sorry to hear that. Could you tell us what made it difficult for you?";
    } else if (worstRating >= 4) {
      return "Great! Can you tell us what worked well for you?";
    } else if (worstRating === 3) {
      return "Was there anything that could have improved your experience?";
    }
    return "";
  };

  const getFeedbackPlaceholder = () => {
    const worstRating = Math.min(taskRating, platformRating);
    if (worstRating <= 2) {
      return "Please describe what was difficult or frustrating...";
    } else if (worstRating >= 4) {
      return "Please tell us what worked well for you...";
    } else if (worstRating === 3) {
      return "Any suggestions for improvement...";
    }
    return "Your feedback...";
  };

  const getEmojiForRating = (ratingValue) => {
    switch (ratingValue) {
      case 1: return "😞"; // Very sad
      case 2: return "🙁";  // Sad
      case 3: return "😐";  // Neutral
      case 4: return "🙂";  // Happy
      case 5: return "😊";  // Very happy
      default: return "😐";
    }
  };

  const getTaskRatingLabel = (ratingValue) => {
    switch (ratingValue) {
      case 1: return "Overwhelming";
      case 2: return "Quite challenging";
      case 3: return "Manageable";
      case 4: return "Quite straightforward";
      case 5: return "Straightforward";
      default: return "";
    }
  };

  const getPlatformRatingLabel = (ratingValue) => {
    switch (ratingValue) {
      case 1: return "Difficult to use";
      case 2: return "Somewhat difficult";
      case 3: return "Okay";
      case 4: return "Quite smooth";
      case 5: return "Very smooth";
      default: return "";
    }
  };

  const renderMainForm = () => (
    <div className="relative">
      {showConfetti && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50">
          <ConfettiExplosion 
            particleCount={200}
            width={1600}
            colors={['#8b5cf6', '#10b981', '#3b82f6', '#f59e0b', '#ef4444']}
            duration={3000}
          />
        </div>
      )}
      
      {/* Header Section */}
      <div className="text-center mb-4">
        {/* <div className="flex justify-center mb-3">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircleIcon className="w-8 h-8 text-green-600" />
          </div>
        </div> */}
        
        <AlertDialog.Title className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 leading-tight">
          Amazing work! 🎉
        </AlertDialog.Title>
        
        {/* Collapsible descriptions on mobile when feedback section is shown */}
        <div className={`
          transition-all duration-500 ease-out overflow-hidden
          ${taskRating > 0 && platformRating > 0 
            ? 'sm:block hidden opacity-0 max-h-0 mb-0' 
            : 'block opacity-100 max-h-[200px]'
          }
        `}>
        <AlertDialog.Description className="text-gray-600 text-sm sm:text-base mb-3 leading-relaxed">
           You've completed all your questionnaires - that's a big achievement!
        </AlertDialog.Description>
                
          <AlertDialog.Description className="text-gray-600 text-sm sm:text-base mb-3 leading-relaxed">
            We know it takes effort, time and focus, especially for people who struggle with forms.
          </AlertDialog.Description>
          
          <AlertDialog.Description className="text-gray-600 text-sm sm:text-base mb-3 leading-relaxed">
            Your feedback will help us improve and make the process easier for others.
          </AlertDialog.Description>
        </div>
      </div>

      {/* Questions Section - Highlighted */}
      <div className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-2xl p-5 mb-6 shadow-sm transition-all duration-500 ease-out overflow-hidden">
        <div className="text-center">
          
          {/* <p className="text-xs sm:text-sm text-gray-600 mb-6">
            Your feedback helps us improve for everyone
          </p> */}
          
          {/* Question 1: The Task */}
          <div className="mb-6">
          <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2">
          The Task: How did you find completing the questionnaires?
          </h4>
            
            
            <div className="flex justify-center gap-2 sm:gap-3 mb-2">
              {[1, 2, 3, 4, 5].map((ratingValue) => (
                <button
                  key={ratingValue}
                  onClick={() => handleTaskRatingSelect(ratingValue)}
                  className={`
                    relative p-2 sm:p-3 rounded-xl transition-all duration-200 ease-out
                    text-2xl sm:text-3xl min-w-[50px] min-h-[50px] sm:min-w-[60px] sm:min-h-[60px]
                    flex items-center justify-center flex-shrink-0
                    ${taskRating === ratingValue 
                      ? 'bg-white shadow-lg transform scale-110 ring-3 ring-violet-300 ring-opacity-50' 
                      : 'bg-white/70 hover:bg-white hover:shadow-md hover:scale-105 active:scale-95'
                    }
                  `}
                  title={getTaskRatingLabel(ratingValue)}
                  aria-label={`Rate ${getTaskRatingLabel(ratingValue)}`}
                >
                  {getEmojiForRating(ratingValue)}
                  {taskRating === ratingValue && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-violet-500 rounded-full flex items-center justify-center">
                      <CheckCircleIcon className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
            
            {/* Labels row for Task question */}
            <div className="flex justify-center gap-2 sm:gap-3">
              {[1, 2, 3, 4, 5].map((ratingValue) => (
                <div
                  key={`task-label-${ratingValue}`}
                  className="min-w-[40px] min-h-[20px] sm:min-w-[55px] flex items-center justify-center flex-shrink-0"
                >
                  {ratingValue === 1 && (
                    <span className="text-xs text-gray-500 text-center">Overwhelming</span>
                  )}
                  {ratingValue === 5 && (
                    <span className="text-xs text-gray-500 text-center">Straightforward</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Question 2: The Platform */}
          <div className="mb-6">
          <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2">
          The Platform: How was your experience using this platform?
          </h4>
            
            
            <div className="flex justify-center gap-2 sm:gap-3 mb-2">
              {[1, 2, 3, 4, 5].map((ratingValue) => (
                <button
                  key={ratingValue}
                  onClick={() => handlePlatformRatingSelect(ratingValue)}
                  className={`
                    relative p-2 sm:p-3 rounded-xl transition-all duration-200 ease-out
                    text-2xl sm:text-3xl min-w-[50px] min-h-[50px] sm:min-w-[60px] sm:min-h-[60px]
                    flex items-center justify-center flex-shrink-0
                    ${platformRating === ratingValue 
                      ? 'bg-white shadow-lg transform scale-110 ring-3 ring-violet-300 ring-opacity-50' 
                      : 'bg-white/70 hover:bg-white hover:shadow-md hover:scale-105 active:scale-95'
                    }
                  `}
                  title={getPlatformRatingLabel(ratingValue)}
                  aria-label={`Rate ${getPlatformRatingLabel(ratingValue)}`}
                >
                  {getEmojiForRating(ratingValue)}
                  {platformRating === ratingValue && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-violet-500 rounded-full flex items-center justify-center">
                      <CheckCircleIcon className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
            
            {/* Labels row for Platform question */}
            <div className="flex justify-center gap-2 sm:gap-3">
              {[1, 2, 3, 4, 5].map((ratingValue) => (
                <div
                  key={`platform-label-${ratingValue}`}
                  className="min-w-[50px] min-h-[20px] sm:min-w-[55px] flex items-center justify-center flex-shrink-0"
                >
                  {ratingValue === 1 && (
                    <span className="text-xs text-gray-500 text-center">Difficult<br className="sm:hidden" /> to use</span>
                  )}
                  {ratingValue === 5 && (
                    <span className="text-xs text-gray-500 text-center">Very<br className="sm:hidden" /> smooth</span>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Feedback section - expands smoothly when both questions are answered */}
          {taskRating > 0 && platformRating > 0 && (
            <div className="animate-slideDown">
              <div className="border-t border-violet-200 pt-4 mt-4">
                <p className="text-sm font-medium text-gray-700 mb-3 text-left">
                  {getFeedbackPrompt()}
                </p>
                
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder={getFeedbackPlaceholder()}
                  className="w-full p-3 border border-gray-300 rounded-lg resize-none max-h-[200px] overflow-y-auto focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all text-sm leading-relaxed"
                  onInput={(e) => resizeTextarea(e.target)}
                  rows={3}
                  autoFocus={true}
                />
                
                {feedbackText.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {feedbackText.length} characters
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons - Always show when both ratings are selected */}
      {taskRating > 0 && platformRating > 0 && (
        <div className="flex flex-col gap-2 animate-slideDown">
          <button
            onClick={handleSubmitFeedback}
            disabled={isSubmitting}
            className="
              w-full py-3 px-4 bg-gradient-to-r from-violet-600 to-violet-700 text-white 
              rounded-lg font-medium hover:from-violet-700 hover:to-violet-800 
              transition-all duration-200 ease-out transform hover:scale-[1.02] active:scale-[0.98]
              disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
              shadow-md hover:shadow-lg
              text-sm
            "
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Submitting...
              </div>
            ) : (
              'Submit Feedback'
            )}
          </button>
        </div>
      )}
    </div>
  );

  const renderSuccessStep = () => (
    <div className="text-center animate-fadeIn">
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircleIcon className="w-10 h-10 text-green-600" />
        </div>
      </div>
      
      <AlertDialog.Title className="text-xl font-bold text-gray-900 mb-4">
        Thank you for giving us feedback!
      </AlertDialog.Title>
      
      <AlertDialog.Description className="text-gray-600 text-base leading-relaxed mb-6">
        We really appreciate you taking the time to help us improve the experience for everyone.
      </AlertDialog.Description>
      
      <button
        onClick={onClose}
        className="w-full py-3 px-4 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 transition-colors"
      >
        Close
      </button>
    </div>
  );

  const renderCurrentStep = () => {
    if (hasSubmitted) return renderSuccessStep();
    return renderMainForm();
  };

  return (
    <>
      {/* Inject custom styles */}
      <style>{customStyles}</style>
      
      <AlertDialog.Root open={isOpen}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 z-50 backdrop-blur-sm" />
          <AlertDialog.Content 
            ref={modalContentRef}
            className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-[95vw] sm:w-[90vw] max-w-2xl max-h-[90vh] overflow-y-auto z-50"
          >
            {renderCurrentStep()}
            
            {/* Show close button always (except when submitting) */}
            {!isSubmitting && (
              <AlertDialog.Cancel asChild>
                <button
                  onClick={handleDismiss}
                  className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </AlertDialog.Cancel>
            )}
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  );
}